(function () {
  const config = window.OpenImmoImporterBlocks || {};
  const favoriteKey = 'openimmo_importer_favorites';
  const scriptNonce = document.currentScript?.nonce || '';
  let maplibregl = null;
  let mapLibrePromise = null;
  let googleMapsApi = null;
  let googleMapsPromise = null;
  let markerClustererPromise = null;
  let turnstilePromise = null;
  const turnstileStates = new WeakMap();
  const idempotencyStates = new WeakMap();
  const busyFormButtons = new WeakMap();
  const googleMapsSettings = config.googleMaps || {};
  const markerMapText = config.markerMapText || {};
  const googleMapsApiKey = String(googleMapsSettings.apiKey || '').trim();
  const turnstileSettings = config.turnstile || {};
  const googleMapsScriptTimeoutMs = 20000;
  const turnstileScriptTimeoutMs = 15000;
  const turnstileExecutionTimeoutMs = 120000;
  const favoriteMaxItems = 50;
  const mapMaxPages = 5;
  const turnstileEnabled = turnstileSettings.enabled === true
    && /^[A-Za-z0-9_-]{1,32}$/.test(String(turnstileSettings.sitekey || ''));

  const loadTurnstile = () => {
    if (!turnstileEnabled) {
      return Promise.resolve(null);
    }

    if (window.turnstile?.render && window.turnstile?.execute) {
      return Promise.resolve(window.turnstile);
    }

    if (!turnstilePromise) {
      turnstilePromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        let settled = false;
        let timeoutId = 0;
        const finish = (callback) => {
          if (settled) {
            return;
          }

          settled = true;
          window.clearTimeout(timeoutId);
          callback();
        };
        timeoutId = window.setTimeout(
          () => finish(() => reject(new Error('Die Sicherheitsprüfung konnte nicht rechtzeitig geladen werden.'))),
          turnstileScriptTimeoutMs
        );
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.dataset.openimmoTurnstile = 'true';

        if (scriptNonce) {
          script.nonce = scriptNonce;
        }

        script.addEventListener('load', () => {
          finish(() => {
            if (!window.turnstile?.render || !window.turnstile?.execute) {
              reject(new Error('Die Sicherheitsprüfung konnte nicht initialisiert werden.'));
              return;
            }

            resolve(window.turnstile);
          });
        }, { once: true });
        script.addEventListener(
          'error',
          () => finish(() => reject(new Error('Die Sicherheitsprüfung konnte nicht geladen werden.'))),
          { once: true }
        );
        document.head.appendChild(script);
      }).catch((error) => {
        turnstilePromise = null;
        throw error;
      });
    }

    return turnstilePromise;
  };

  const settleTurnstile = (state, token = '', error = null) => {
    if (!state.resolve && !state.reject) {
      if (!state.executed) {
        state.pendingError = error || new Error('Die Sicherheitsprüfung konnte nicht abgeschlossen werden.');
      }

      return;
    }

    window.clearTimeout(state.timeoutId);
    state.timeoutId = 0;
    const resolve = state.resolve;
    const reject = state.reject;
    state.resolve = null;
    state.reject = null;

    if (error || typeof token !== 'string' || token === '') {
      reject?.(error || new Error('Die Sicherheitsprüfung ist abgelaufen. Bitte versuchen Sie es erneut.'));
      return;
    }

    resolve?.(token);
  };

  const renderTurnstile = (api, form, action) => {
    const previous = turnstileStates.get(form);

    if (previous?.widgetId !== undefined) {
      api.remove?.(previous.widgetId);
    }

    const container = document.createElement('div');
    container.className = 'openimmo-turnstile';
    container.dataset.action = action;
    form.appendChild(container);

    const state = {
      action,
      container,
      executed: false,
      pendingError: null,
      promise: null,
      reject: null,
      resolve: null,
      timeoutId: 0,
      widgetId: undefined
    };
    state.widgetId = api.render(container, {
      sitekey: String(turnstileSettings.sitekey),
      action,
      appearance: 'interaction-only',
      execution: 'execute',
      retry: 'never',
      'refresh-expired': 'never',
      'refresh-timeout': 'never',
      'response-field': false,
      callback: (token) => settleTurnstile(state, token),
      'error-callback': () => settleTurnstile(
        state,
        '',
        new Error('Die Sicherheitsprüfung ist fehlgeschlagen. Bitte versuchen Sie es erneut.')
      ),
      'expired-callback': () => settleTurnstile(
        state,
        '',
        new Error('Die Sicherheitsprüfung ist abgelaufen. Bitte versuchen Sie es erneut.')
      ),
      'timeout-callback': () => settleTurnstile(
        state,
        '',
        new Error('Die Sicherheitsprüfung hat zu lange gedauert. Bitte versuchen Sie es erneut.')
      ),
      'unsupported-callback': () => settleTurnstile(
        state,
        '',
        new Error('Ihr Browser unterstützt die Sicherheitsprüfung nicht. Bitte verwenden Sie einen aktuellen Browser.')
      )
    });
    turnstileStates.set(form, state);

    return state;
  };

  const executeTurnstile = async (form, action) => {
    if (!turnstileEnabled) {
      return null;
    }

    const api = await loadTurnstile();
    let state = turnstileStates.get(form);

    if (
      !state
      || state.action !== action
      || !state.container?.isConnected
      || state.widgetId === undefined
    ) {
      state = renderTurnstile(api, form, action);
    }

    if (state.promise) {
      return state.promise;
    }

    if (state.executed) {
      api.reset(state.widgetId);
    }

    state.executed = true;
    state.promise = new Promise((resolve, reject) => {
      state.resolve = resolve;
      state.reject = reject;
      state.timeoutId = window.setTimeout(
        () => settleTurnstile(
          state,
          '',
          new Error('Die Sicherheitsprüfung hat zu lange gedauert. Bitte versuchen Sie es erneut.')
        ),
        turnstileExecutionTimeoutMs
      );

      if (state.pendingError) {
        const pendingError = state.pendingError;
        state.pendingError = null;
        settleTurnstile(state, '', pendingError);
        return;
      }

      try {
        api.execute(state.widgetId);
      } catch (error) {
        settleTurnstile(
          state,
          '',
          new Error('Die Sicherheitsprüfung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.')
        );
      }
    }).finally(() => {
      state.promise = null;
    });

    return state.promise;
  };

  const protectedPayload = async (form, action, payload) => {
    const token = await executeTurnstile(form, action);

    return token === null ? payload : { ...payload, 'cf-turnstile-response': token };
  };

  const idempotencyKey = (form, action) => {
    const state = idempotencyStates.get(form) || {};

    if (!state[action]) {
      state[action] = window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
      idempotencyStates.set(form, state);
    }

    return state[action];
  };

  const clearIdempotencyKey = (form, action) => {
    const state = idempotencyStates.get(form);

    if (state) {
      delete state[action];
    }
  };

  const idempotentPayload = (form, action, payload) => ({
    ...payload,
    idempotency_key: idempotencyKey(form, action)
  });

  const loadMapLibre = () => {
    if (window.maplibregl) {
      maplibregl = window.maplibregl;

      return Promise.resolve(maplibregl);
    }

    if (!mapLibrePromise) {
      mapLibrePromise = new Promise((resolve, reject) => {
        const source = String(config.mapLibreUrl || '');
        if (!source) {
          reject(new Error('Missing local MapLibre bundle URL'));
          return;
        }

        const script = document.createElement('script');
        script.src = source;
        script.async = true;
        script.dataset.openimmoMaplibre = 'true';
        if (scriptNonce) {
          script.nonce = scriptNonce;
        }
        script.addEventListener('load', () => {
          if (!window.maplibregl) {
            reject(new Error('Invalid local MapLibre bundle'));
            return;
          }

          maplibregl = window.maplibregl;
          resolve(maplibregl);
        }, { once: true });
        script.addEventListener('error', () => reject(new Error('Local MapLibre bundle failed to load')), { once: true });
        document.head.appendChild(script);
      });
    }

    return mapLibrePromise;
  };

  const loadGoogleMarkerClusterer = () => {
    if (window.markerClusterer?.MarkerClusterer) {
      return Promise.resolve(window.markerClusterer.MarkerClusterer);
    }

    if (!markerClustererPromise) {
      markerClustererPromise = new Promise((resolve) => {
        const source = String(googleMapsSettings.markerClustererUrl || '');

        if (!source) {
          resolve(null);
          return;
        }

        const script = document.createElement('script');
        script.src = source;
        script.async = true;
        script.dataset.openimmoMarkerClusterer = 'true';
        if (scriptNonce) {
          script.nonce = scriptNonce;
        }
        script.addEventListener('load', () => resolve(window.markerClusterer?.MarkerClusterer || null), { once: true });
        script.addEventListener('error', () => resolve(null), { once: true });
        document.head.appendChild(script);
      }).catch(() => null);
    }

    return markerClustererPromise;
  };

  const loadGoogleMaps = () => {
    if (window.google?.maps?.importLibrary) {
      googleMapsApi = window.google.maps;

      return Promise.resolve(googleMapsApi);
    }

    if (!googleMapsApiKey) {
      return Promise.reject(new Error('Missing Google Maps API key'));
    }

    if (!googleMapsPromise) {
      googleMapsPromise = new Promise((resolve, reject) => {
        const callbackName = `openImmoGoogleMapsReady${Date.now().toString(36)}`;
        let settled = false;
        let timeoutId = 0;
        const previousAuthFailure = typeof window.gm_authFailure === 'function'
          ? window.gm_authFailure
          : null;
        const cleanup = () => {
          delete window[callbackName];

          if (window.gm_authFailure === authFailureHandler) {
            if (previousAuthFailure) {
              window.gm_authFailure = previousAuthFailure;
            } else {
              delete window.gm_authFailure;
            }
          }
        };
        const finish = (callback) => {
          if (settled) {
            return;
          }

          settled = true;
          window.clearTimeout(timeoutId);
          cleanup();
          callback();
        };
        const authFailureHandler = () => finish(
          () => reject(new Error('Google Maps authorization failed'))
        );

        timeoutId = window.setTimeout(
          () => finish(() => reject(new Error('Google Maps API timed out'))),
          googleMapsScriptTimeoutMs
        );
        window.gm_authFailure = authFailureHandler;
        window[callbackName] = async () => {
          try {
            if (!window.google?.maps?.importLibrary) {
              throw new Error('Google Maps API missing importLibrary');
            }

            await Promise.all([
              window.google.maps.importLibrary('maps'),
              window.google.maps.importLibrary('marker')
            ]);
            finish(() => {
              googleMapsApi = window.google.maps;
              resolve(googleMapsApi);
            });
          } catch (error) {
            finish(() => reject(new Error('Google Maps API could not be initialized')));
          }
        };

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(googleMapsApiKey)}&loading=async&callback=${encodeURIComponent(callbackName)}&v=weekly`;
        script.async = true;
        script.defer = true;
        script.dataset.openimmoGoogleMaps = 'true';
        if (scriptNonce) {
          script.nonce = scriptNonce;
        }
        script.addEventListener(
          'error',
          () => finish(() => reject(new Error('Google Maps API failed to load'))),
          { once: true }
        );
        document.head.appendChild(script);
      }).catch((error) => {
        googleMapsApi = null;
        googleMapsPromise = null;
        throw error;
      });
    }

    return googleMapsPromise;
  };

  const normalizeFavoriteIds = (items) => [...new Set(
    (Array.isArray(items) ? items : [])
      .map((item) => Number(item))
      .filter((item) => Number.isSafeInteger(item) && item > 0)
  )].slice(0, favoriteMaxItems);

  const readFavorites = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(favoriteKey) || '[]');

      return normalizeFavoriteIds(value);
    } catch (error) {
      return [];
    }
  };

  const writeFavorites = (items) => {
    window.localStorage.setItem(favoriteKey, JSON.stringify(normalizeFavoriteIds(items)));
  };

  const setFeedback = (form, message, type = 'success') => {
    const feedback = form.querySelector('.openimmo-contact-form__feedback, .openimmo-search-agent-form__feedback');

    if (!feedback) {
      return;
    }

    feedback.hidden = false;
    feedback.textContent = message;
    feedback.dataset.type = type;
  };

  const setFormButtonBusy = (form, busy, trigger = null) => {
    if (busy) {
      const button = trigger?.matches?.('button')
        ? trigger
        : form.querySelector('button[type="submit"]');

      if (button) {
        busyFormButtons.set(form, button);
        button.classList?.toggle?.('is-loading', true);
        button.setAttribute?.('aria-busy', 'true');
      }
      return;
    }

    const button = busyFormButtons.get(form);

    if (button) {
      button.classList?.toggle?.('is-loading', false);
      button.removeAttribute?.('aria-busy');
    }

    busyFormButtons.delete(form);
  };

  const setPublicFormBusy = (form, busy, trigger = null) => {
    if (busy) {
      form.setAttribute('aria-busy', 'true');
    } else {
      form.removeAttribute('aria-busy');
    }
    const submit = form.querySelector('button[type="submit"]');

    if (submit) {
      submit.disabled = busy;
    }
    setFormButtonBusy(form, busy, trigger);
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const pdfExposeStates = new WeakMap();

  const pdfExposeRequest = async (path, payload) => {
    let response;

    try {
      response = await fetch(`${config.restUrl}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });
    } catch (error) {
      throw new Error('Der Server ist nicht erreichbar. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.');
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.message || 'Die Exposé-Freigabe konnte nicht verarbeitet werden.');
    }

    return data;
  };

  const setPdfExposeBusy = (form, busy, trigger = null) => {
    if (busy) {
      form.setAttribute('aria-busy', 'true');
    } else {
      form.removeAttribute('aria-busy');
    }
    form.querySelectorAll('button, input').forEach((control) => {
      control.disabled = busy;
    });
    setFormButtonBusy(form, busy, trigger);
  };

  const setPdfExposeFeedback = (form, message = '', type = 'error') => {
    const feedback = form.querySelector('.openimmo-pdf-expose__feedback');

    if (!feedback) {
      return;
    }

    feedback.hidden = message === '';
    feedback.textContent = message;
    feedback.dataset.type = type;

    if (message !== '') {
      feedback.setAttribute('tabindex', '-1');
      feedback.focus();
    }
  };

  const togglePdfExposeCustomerType = (form) => {
    const business = form.querySelector('[name="business"]:checked')?.value === '1';
    const company = form.querySelector('[data-openimmo-company-field]');
    const companyInput = company?.querySelector('input');
    const addressInputs = form.querySelectorAll('[data-openimmo-address-field]');

    company?.classList.toggle('is-hidden', !business);

    if (companyInput) {
      companyInput.required = business;
    }

    addressInputs.forEach((input) => {
      input.required = form.dataset.hasCourtage === '1';
    });

    const container = form.closest('.openimmo-pdf-expose') || form;

    container.querySelectorAll('[data-openimmo-consumer-only]').forEach((element) => {
      element.classList.toggle('is-hidden', business);
      const input = element.matches('label') ? element.querySelector('input') : null;

      if (input) {
        input.required = !business;
      }
    });

    container.querySelectorAll('[data-openimmo-business-only]').forEach((element) => {
      element.classList.toggle('is-hidden', !business);
      const input = element.matches('label') ? element.querySelector('input') : null;

      if (input) {
        input.required = business;
      }
    });
  };

  const joinPdfExposeParts = (...parts) => parts
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' ');

  const pdfExposeName = (form) => joinPdfExposeParts(
    form.querySelector('[name="requester_first_name"]')?.value || '',
    form.querySelector('[name="requester_last_name"]')?.value || ''
  );

  const pdfExposeAddress = (form) => {
    const streetLine = joinPdfExposeParts(
      form.querySelector('[name="requester_street"]')?.value || '',
      form.querySelector('[name="requester_house_number"]')?.value || ''
    );
    const cityLine = joinPdfExposeParts(
      form.querySelector('[name="requester_postal_code"]')?.value || '',
      form.querySelector('[name="requester_city"]')?.value || ''
    );

    return [streetLine, cityLine].filter(Boolean).join(', ');
  };

  const pdfExposePayload = (form) => {
    const confirmations = {};
    const business = form.querySelector('[name="business"]:checked')?.value === '1';

    form.querySelectorAll('input[name^="confirmation_"]').forEach((input) => {
      const key = input.name.replace(/^confirmation_/, '');
      const consumerOnly = input.closest('[data-openimmo-consumer-only]');
      const businessOnly = input.closest('[data-openimmo-business-only]');

      if (
        !consumerOnly?.classList.contains('is-hidden')
        && !businessOnly?.classList.contains('is-hidden')
      ) {
        confirmations[key] = input.checked;
      }
    });

    return {
      legal_version: business
        ? form.dataset.legalVersionBusiness || ''
        : form.dataset.legalVersionConsumer || '',
      context: {
        address: form.querySelector('[name="address"]')?.value || '',
        activity: form.querySelector('[name="activity"]')?.value || '',
        business,
        requester_name: pdfExposeName(form),
        requester_first_name: form.querySelector('[name="requester_first_name"]')?.value || '',
        requester_last_name: form.querySelector('[name="requester_last_name"]')?.value || '',
        requester_company: form.querySelector('[name="requester_company"]')?.value || '',
        requester_email: form.querySelector('[name="requester_email"]')?.value || '',
        requester_address: pdfExposeAddress(form),
        requester_street: form.querySelector('[name="requester_street"]')?.value || '',
        requester_house_number: form.querySelector('[name="requester_house_number"]')?.value || '',
        requester_postal_code: form.querySelector('[name="requester_postal_code"]')?.value || '',
        requester_city: form.querySelector('[name="requester_city"]')?.value || '',
        requester_phone: form.querySelector('[name="requester_phone"]')?.value || '',
        website: form.querySelector('[name="website"]')?.value || ''
      },
      confirmations
    };
  };

  const renderPdfExposeVerification = (form, authorization) => {
    const state = pdfExposeStates.get(form) || {};
    const hasCourtage = form.dataset.hasCourtage === '1';
    state.challenge = authorization.challenge || '';
    pdfExposeStates.set(form, state);
    form.dataset.stage = 'verify';
    form.innerHTML = `
      <div class="openimmo-pdf-expose__step-heading">
        <span>2</span>
        <div>
          <strong>E-Mail-Adresse bestätigen</strong>
          <small data-openimmo-verification-email></small>
        </div>
      </div>
      <label class="openimmo-pdf-expose__input openimmo-pdf-expose__otp">
        <span>Sechsstelliger Einmalcode</span>
        <input type="text" name="otp_code" inputmode="numeric" autocomplete="one-time-code" pattern="[0-9]{6}" minlength="6" maxlength="6" required autofocus>
      </label>
      <div class="openimmo-pdf-expose__feedback" role="status" aria-live="polite" hidden></div>
      <div class="openimmo-pdf-expose__contract-summary" data-openimmo-contract-summary hidden>
        <strong>Ihr Maklervertrag auf einen Blick</strong>
        <dl>
          <div><dt>Maklerleistung</dt><dd data-openimmo-contract-service></dd></div>
          <div><dt>Immobilie</dt><dd data-openimmo-contract-property></dd></div>
          <div><dt>Objektnummer</dt><dd data-openimmo-contract-object-number></dd></div>
          <div><dt>Hauptvertrag</dt><dd data-openimmo-main-contract-type></dd></div>
          <div><dt>Außencourtage</dt><dd data-openimmo-contract-courtage></dd></div>
          <div><dt>Zahlung</dt><dd data-openimmo-payment-terms></dd></div>
          <div><dt>Laufzeit und Beendigung</dt><dd data-openimmo-contract-duration></dd></div>
        </dl>
      </div>
      <button class="wp-element-button openimmo-pdf-expose__button" type="submit">Code bestätigen und Exposé freigeben</button>
      <div class="openimmo-pdf-expose__secondary-actions">
        <button type="button" data-openimmo-pdf-action="resend">Code erneut senden</button>
        <button type="button" data-openimmo-pdf-action="edit">Angaben ändern</button>
      </div>
    `;
    const verificationEmail = form.querySelector('[data-openimmo-verification-email]');
    const contractSummary = form.querySelector('[data-openimmo-contract-summary]');
    const submit = form.querySelector('button[type="submit"]');
    const summaryValues = new Map([
      ['[data-openimmo-contract-service]', form.dataset.contractService || 'Nicht angegeben'],
      ['[data-openimmo-contract-property]', form.dataset.propertyTitle || 'Nicht angegeben'],
      ['[data-openimmo-contract-object-number]', form.dataset.objectNumber || 'Nicht angegeben'],
      ['[data-openimmo-main-contract-type]', form.dataset.mainContractType || 'Nicht angegeben'],
      ['[data-openimmo-contract-courtage]', form.dataset.courtage || 'Nicht angegeben'],
      ['[data-openimmo-payment-terms]', form.dataset.paymentTerms || 'Nicht angegeben'],
      ['[data-openimmo-contract-duration]', form.dataset.contractDuration || 'Nicht angegeben']
    ]);

    if (verificationEmail) {
      verificationEmail.textContent = `Der Code wurde an ${authorization.masked_email || 'Ihre E-Mail-Adresse'} gesendet und ist 10 Minuten gültig.`;
    }

    summaryValues.forEach((value, selector) => {
      const node = contractSummary?.querySelector(selector);

      if (node) {
        node.textContent = value;
      }
    });

    if (contractSummary) {
      contractSummary.hidden = !hasCourtage;
    }

    if (submit) {
      submit.textContent = hasCourtage
        ? 'Zahlungspflichtigen Maklervertrag abschließen'
        : 'Code bestätigen und Exposé freigeben';
    }

    form.querySelector('[name="otp_code"]')?.focus();
  };

  const renderPdfExposeDownloads = (form, authorization) => {
    const files = Array.isArray(authorization.files) ? authorization.files : [];
    const downloadLabel = form.dataset.downloadLabel || 'PDF herunterladen';
    const items = files.map((file) => `
      <li>
        <span class="openimmo-pdf-expose__file-icon">PDF</span>
        <span><strong>${escapeHtml(file.label || file.filename || 'PDF-Exposé')}</strong><small>${escapeHtml(file.filename || '')}</small></span>
        <a class="wp-element-button openimmo-pdf-expose__button" href="${escapeHtml(file.download_url || '')}">${escapeHtml(downloadLabel)}</a>
      </li>
    `).join('');

    form.dataset.stage = 'complete';
    form.innerHTML = `
      <div class="openimmo-pdf-expose__success" role="status">
        <strong>E-Mail bestätigt. Das Exposé ist freigeschaltet.</strong>
        <span>Wir haben Ihnen eine Bestätigung Ihrer Erklärungen per E-Mail gesendet.</span>
      </div>
      <div class="openimmo-pdf-expose__downloads"><ul>${items}</ul></div>
    `;
  };

  const requestPdfExposeCode = async (form, payload) => {
    const postId = Number(form.dataset.postId || 0);
    const requestPayload = await protectedPayload(
      form,
      'pdf_expose_authorize',
      idempotentPayload(form, 'pdf_expose_authorize', payload)
    );
    const authorization = await pdfExposeRequest(
      `/pdf-expose/properties/${postId}/authorizations`,
      requestPayload
    );
    clearIdempotencyKey(form, 'pdf_expose_authorize');
    const state = pdfExposeStates.get(form) || {};
    state.payload = payload;
    pdfExposeStates.set(form, state);
    renderPdfExposeVerification(form, authorization);
  };

  const verifyPdfExposeCode = async (form) => {
    const state = pdfExposeStates.get(form) || {};
    const postId = Number(form.dataset.postId || 0);
    const code = form.querySelector('[name="otp_code"]')?.value || '';
    const authorization = await pdfExposeRequest(
      `/pdf-expose/properties/${postId}/authorizations/${encodeURIComponent(state.challenge || '')}/verify`,
      { code }
    );

    renderPdfExposeDownloads(form, authorization);
  };

  const initPdfExposeForms = () => {
    document.querySelectorAll('[data-openimmo-pdf-expose-form]').forEach((form) => {
      pdfExposeStates.set(form, { initialMarkup: form.innerHTML, payload: null, challenge: '' });
      togglePdfExposeCustomerType(form);

      form.addEventListener('change', (event) => {
        if (event.target.matches('[name="business"]')) {
          togglePdfExposeCustomerType(form);
        }
      });

      form.addEventListener('click', async (event) => {
        const action = event.target.closest('[data-openimmo-pdf-action]');

        if (!action) {
          return;
        }

        event.preventDefault();

        if (form.hasAttribute('aria-busy')) {
          return;
        }

        const state = pdfExposeStates.get(form) || {};

        if (action.dataset.openimmoPdfAction === 'edit') {
          form.innerHTML = state.initialMarkup || '';
          form.dataset.stage = 'request';
          togglePdfExposeCustomerType(form);
          return;
        }

        if (action.dataset.openimmoPdfAction !== 'resend' || !state.payload) {
          return;
        }

        setPdfExposeBusy(form, true, action);

        try {
          await requestPdfExposeCode(form, state.payload);
        } catch (error) {
          setPdfExposeFeedback(form, error.message || 'Der Einmalcode konnte nicht erneut versendet werden.');
        } finally {
          setPdfExposeBusy(form, false);
        }
      });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        if (form.hasAttribute('aria-busy')) {
          return;
        }

        setPdfExposeFeedback(form);
        setPdfExposeBusy(form, true, event.submitter);

        try {
          if (form.dataset.stage === 'verify') {
            await verifyPdfExposeCode(form);
          } else {
            await requestPdfExposeCode(form, pdfExposePayload(form));
          }
        } catch (error) {
          setPdfExposeFeedback(form, error.message || 'Die Exposé-Freigabe konnte nicht verarbeitet werden.');
        } finally {
          setPdfExposeBusy(form, false);
        }
      });
    });
  };

  const withdrawalStates = new WeakMap();
  const withdrawalStatement = 'Hiermit widerrufe ich den von mir abgeschlossenen Vertrag über die Erbringung der angegebenen Maklerdienstleistung.';

  const withdrawalPayload = (form) => ({
    consumer_name: form.querySelector('[name="consumer_name"]')?.value.trim() || '',
    consumer_address: form.querySelector('[name="consumer_address"]')?.value.trim() || '',
    consumer_email: form.querySelector('[name="consumer_email"]')?.value.trim() || '',
    contract_identifier: form.querySelector('[name="contract_identifier"]')?.value.trim() || '',
    contract_date: form.querySelector('[name="contract_date"]')?.value || '',
    website: form.querySelector('[name="website"]')?.value || ''
  });

  const setWithdrawalBusy = (form, busy, trigger = null) => {
    if (busy) {
      form.setAttribute('aria-busy', 'true');
    } else {
      form.removeAttribute('aria-busy');
    }
    form.querySelectorAll('button, input, textarea').forEach((control) => {
      control.disabled = busy;
    });
    const submit = form.querySelector('button[type="submit"]');
    const icon = submit?.querySelector('wa-icon');
    setFormButtonBusy(form, busy, trigger);

    if (icon) {
      if (busy) {
        icon.dataset.previousName = icon.getAttribute('name') || 'check';
        icon.setAttribute('name', 'circle');
      } else {
        icon.setAttribute('name', icon.dataset.previousName || 'check');
      }
    }
  };

  const setWithdrawalFeedback = (form, message = '', type = 'error') => {
    const feedback = form.querySelector('.openimmo-withdrawal__feedback');

    if (!feedback) {
      return;
    }

    feedback.hidden = message === '';
    feedback.textContent = message;
    feedback.dataset.type = type;

    if (message !== '') {
      feedback.setAttribute('tabindex', '-1');
      feedback.focus();
    }
  };

  const formatWithdrawalAddress = (value) => escapeHtml(value).replace(/\r?\n/g, '<br>');

  const renderWithdrawalReview = (form, payload) => {
    form.dataset.stage = 'review';
    form.innerHTML = `
      <div class="openimmo-withdrawal__step-heading">
        <span>2</span>
        <div>
          <strong>Widerruf prüfen und bestätigen</strong>
          <small>Prüfen Sie Ihre Angaben. Mit dem folgenden Button übermitteln Sie den Widerruf.</small>
        </div>
      </div>
      <blockquote class="openimmo-withdrawal__statement">${escapeHtml(withdrawalStatement)}</blockquote>
      <dl class="openimmo-withdrawal__review">
        <div><dt>Name</dt><dd>${escapeHtml(payload.consumer_name)}</dd></div>
        <div><dt>Anschrift</dt><dd>${formatWithdrawalAddress(payload.consumer_address)}</dd></div>
        <div><dt>E-Mail</dt><dd>${escapeHtml(payload.consumer_email)}</dd></div>
        <div><dt>Vertrag oder Immobilie</dt><dd>${escapeHtml(payload.contract_identifier)}</dd></div>
        ${payload.contract_date ? `<div><dt>Vertragsdatum</dt><dd>${escapeHtml(payload.contract_date)}</dd></div>` : ''}
      </dl>
      <div class="openimmo-withdrawal__feedback" role="status" aria-live="polite" hidden></div>
      <div class="openimmo-withdrawal__actions">
        <button class="wp-element-button openimmo-withdrawal__button" type="submit">
          <wa-icon name="check" library="system" variant="solid" aria-hidden="true"></wa-icon>
          <span>Widerruf bestätigen</span>
        </button>
        <button class="wp-element-button openimmo-withdrawal__button openimmo-withdrawal__button--secondary" type="button" data-openimmo-withdrawal-action="edit">
          <wa-icon name="gear" library="system" variant="solid" aria-hidden="true"></wa-icon>
          <span>Angaben ändern</span>
        </button>
      </div>
    `;
    form.querySelector('button[type="submit"]')?.focus();
  };

  const restoreWithdrawalForm = (form) => {
    const state = withdrawalStates.get(form) || {};
    const payload = state.payload || {};

    form.innerHTML = state.initialMarkup || '';
    form.dataset.stage = 'input';
    clearIdempotencyKey(form, 'withdrawal_submit');

    Object.entries(payload).forEach(([name, value]) => {
      const field = form.querySelector(`[name="${name}"]`);
      if (field) {
        field.value = value;
      }
    });
    form.querySelector('[name="consumer_name"]')?.focus();
  };

  const submitWithdrawal = async (form, payload) => {
    let response;
    const requestPayload = await protectedPayload(form, 'withdrawal_submit', {
      ...idempotentPayload(form, 'withdrawal_submit', payload),
      confirmed: true
    });

    try {
      response = await fetch(`${config.restUrl}/legal/withdrawals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(requestPayload)
      });
    } catch (error) {
      throw new Error('Der Server ist nicht erreichbar. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.');
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.message || 'Der Widerruf konnte nicht übermittelt werden.');
    }

    clearIdempotencyKey(form, 'withdrawal_submit');
    form.dataset.stage = 'complete';
    const receiptText = typeof data.receipt_text === 'string' ? data.receipt_text : '';
    form.innerHTML = `
      <div class="openimmo-withdrawal__success" role="status" tabindex="-1">
        <wa-icon name="check" library="system" variant="solid" aria-hidden="true"></wa-icon>
        <div>
          <strong>Ihr Widerruf ist eingegangen.</strong>
          <p>${escapeHtml(data.message || 'Der Widerruf wurde übermittelt.')}</p>
          <dl>
            <div><dt>Vorgangsnummer</dt><dd>${escapeHtml(data.receipt || '')}</dd></div>
            <div><dt>Eingang</dt><dd>${escapeHtml(data.submitted_at || '')}</dd></div>
          </dl>
          <a class="wp-element-button openimmo-withdrawal__button" data-openimmo-withdrawal-receipt href="#">Eingangsbestätigung herunterladen</a>
          ${data.confirmation_sent && data.recipient_notified ? '' : '<p class="openimmo-withdrawal__delivery-warning">Mindestens eine E-Mail konnte nicht versendet werden. Laden Sie die Eingangsbestätigung herunter und bewahren Sie sie auf.</p>'}
        </div>
      </div>
    `;
    const receiptLink = form.querySelector('[data-openimmo-withdrawal-receipt]');

    if (receiptLink && receiptText !== '') {
      const receiptFile = new Blob([receiptText], { type: 'text/plain;charset=utf-8' });
      const receiptName = String(data.receipt || 'widerruf').replace(/[^a-z0-9_-]+/gi, '-');
      receiptLink.href = URL.createObjectURL(receiptFile);
      receiptLink.download = `eingangsbestaetigung-${receiptName}.txt`;
    } else if (receiptLink) {
      receiptLink.hidden = true;
    }
    form.querySelector('.openimmo-withdrawal__success')?.focus();
  };

  const initWithdrawalForms = () => {
    document.querySelectorAll('[data-openimmo-withdrawal-form]').forEach((form) => {
      withdrawalStates.set(form, { initialMarkup: form.innerHTML, payload: null });

      form.addEventListener('click', (event) => {
        const action = event.target.closest('[data-openimmo-withdrawal-action]');

        if (!action || action.dataset.openimmoWithdrawalAction !== 'edit') {
          return;
        }

        event.preventDefault();
        restoreWithdrawalForm(form);
      });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        if (form.hasAttribute('aria-busy')) {
          return;
        }

        if (form.dataset.stage === 'input') {
          if (!form.checkValidity()) {
            form.reportValidity();
            return;
          }

          const payload = withdrawalPayload(form);
          const state = withdrawalStates.get(form) || {};
          state.payload = payload;
          withdrawalStates.set(form, state);
          renderWithdrawalReview(form, payload);
          return;
        }

        if (form.dataset.stage !== 'review') {
          return;
        }

        const state = withdrawalStates.get(form) || {};
        setWithdrawalFeedback(form);
        setWithdrawalBusy(form, true, event.submitter);

        try {
          await submitWithdrawal(form, state.payload || {});
        } catch (error) {
          setWithdrawalFeedback(form, error.message || 'Der Widerruf konnte nicht übermittelt werden.');
        } finally {
          setWithdrawalBusy(form, false);
        }
      });
    });
  };

  const initContactForms = () => {
    document.querySelectorAll('[data-openimmo-contact-form]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        if (form.hasAttribute('aria-busy')) {
          return;
        }

        setPublicFormBusy(form, true, event.submitter);
        try {
          const payload = await protectedPayload(
            form,
            'contact_inquiry',
            idempotentPayload(
              form,
              'contact_inquiry',
              Object.fromEntries(new FormData(form).entries())
            )
          );
          const response = await fetch(`${config.restUrl}/features/contact/inquiries`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Die Anfrage konnte nicht versendet werden.');
          }

          clearIdempotencyKey(form, 'contact_inquiry');
          form.reset();
          setFeedback(form, form.dataset.successMessage || 'Vielen Dank. Ihre Anfrage wurde gesendet.');
        } catch (error) {
          setFeedback(form, error.message || 'Die Anfrage konnte nicht versendet werden.', 'error');
        } finally {
          setPublicFormBusy(form, false);
        }
      });
    });
  };

  const initSearchAgentForms = () => {
    document.querySelectorAll('[data-openimmo-search-agent-form]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        if (form.hasAttribute('aria-busy')) {
          return;
        }

        setPublicFormBusy(form, true, event.submitter);
        try {
          const payload = await protectedPayload(
            form,
            'search_agent_subscribe',
            Object.fromEntries(new FormData(form).entries())
          );
          const response = await fetch(`${config.restUrl}/features/search-agents/subscriptions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Der Suchagent konnte nicht eingerichtet werden.');
          }

          form.reset();
          setFeedback(
            form,
            form.dataset.successMessage || data.message || 'Bitte bestätigen Sie Ihren Suchagenten über den Link in der Bestätigungs-E-Mail.'
          );
        } catch (error) {
          setFeedback(form, error.message || 'Der Suchagent konnte nicht eingerichtet werden.', 'error');
        } finally {
          setPublicFormBusy(form, false);
        }
      });
    });
  };

  const updateFavoriteButtons = () => {
    const favorites = readFavorites();

    document.querySelectorAll('[data-openimmo-favorite-button]').forEach((button) => {
      const postId = Number(button.dataset.postId);
      const active = favorites.includes(postId);
      const label = active ? button.dataset.activeLabel : button.dataset.label;
      const labelNode = button.querySelector('.openimmo-favorite-button__label');

      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');

      if (labelNode) {
        labelNode.textContent = label || '';
      }
    });
  };

  // REST and data-* values are untrusted: validate URLs and never parse them as HTML.
  const safeFavoriteUrl = (value, { sameOrigin = false } = {}) => {
    if (typeof value !== 'string' || value.trim() === '') {
      return '';
    }

    try {
      const url = new URL(value, window.location.href);
      const httpAllowed = window.location.protocol === 'http:' && url.protocol === 'http:';
      const protocolAllowed = url.protocol === 'https:' || httpAllowed;

      if (!protocolAllowed || (sameOrigin && url.origin !== window.location.origin)) {
        return '';
      }

      return url.href;
    } catch (error) {
      return '';
    }
  };

  const renderFavoriteEmptyState = (list) => {
    const empty = document.createElement('p');
    empty.className = 'openimmo-favorite-list__empty';
    empty.textContent = list.dataset.emptyMessage || '';
    list.replaceChildren(empty);
  };

  const appendFavoriteItem = (list, item, actionLabel) => {
    if (!item || typeof item !== 'object') {
      return;
    }

    const article = document.createElement('article');
    const content = document.createElement('div');
    const heading = document.createElement('strong');
    const permalink = safeFavoriteUrl(item.permalink, { sameOrigin: true });
    const imageUrl = safeFavoriteUrl(item.image);

    article.className = 'openimmo-favorite-list__item';

    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = '';
      article.appendChild(image);
    }

    if (permalink) {
      const titleLink = document.createElement('a');
      titleLink.href = permalink;
      titleLink.textContent = item.title || '';
      heading.appendChild(titleLink);
    } else {
      heading.textContent = item.title || '';
    }

    content.appendChild(heading);

    [item.location, item.price].forEach((value) => {
      if (!value) {
        return;
      }

      const detail = document.createElement('span');
      detail.textContent = value;
      content.appendChild(detail);
    });

    if (permalink) {
      const action = document.createElement('a');
      action.className = 'wp-element-button openimmo-favorite-list__action';
      action.href = permalink;
      action.textContent = actionLabel;
      content.appendChild(action);
    }

    article.appendChild(content);
    list.appendChild(article);
  };

  const renderFavoriteLists = async () => {
    const favorites = readFavorites();

    document.querySelectorAll('[data-openimmo-favorite-list]').forEach(async (list) => {
      if (!favorites.length) {
        renderFavoriteEmptyState(list);
        return;
      }

      try {
        const response = await fetch(`${config.restUrl}/features/favorites/objects?ids=${encodeURIComponent(favorites.join(','))}`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        const data = await response.json().catch(() => ({}));
        const objects = response.ok && Array.isArray(data.objects) ? data.objects : [];

        if (!objects.length) {
          renderFavoriteEmptyState(list);
          return;
        }

        const actionLabel = list.dataset.actionLabel || 'Details ansehen';
        list.replaceChildren();
        objects.forEach((item) => appendFavoriteItem(list, item, actionLabel));

        if (!list.childElementCount) {
          renderFavoriteEmptyState(list);
        }
      } catch (error) {
        renderFavoriteEmptyState(list);
      }
    });
  };

  const initFavorites = () => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-openimmo-favorite-button]');

      if (!button) {
        return;
      }

      const postId = Number(button.dataset.postId);
      const favorites = readFavorites();
      const next = favorites.includes(postId)
        ? favorites.filter((item) => item !== postId)
        : [...favorites, postId];

      writeFavorites(next);
      updateFavoriteButtons();
      renderFavoriteLists();
    });

    updateFavoriteButtons();
    renderFavoriteLists();
  };

  const mapText = {
    empty: 'Für die Karte sind keine Standortdaten verfügbar.',
    error: 'Die Karte konnte nicht geladen werden. Die Immobilienliste bleibt verfügbar.',
    missingStyle: 'Die Karte ist noch nicht vollständig eingerichtet. Die Immobilienliste bleibt verfügbar.',
    approximate: 'Ungefähre Lage',
    details: 'Immobilie ansehen',
    oneResult: '1 Immobilie auf der Karte',
    manyResults: (count) => `${count} Immobilien auf der Karte`,
    truncated: (count) => `Es werden die ersten ${count} Immobilien angezeigt.`
  };

  const setMapStatus = (section, message = '', type = '') => {
    const status = section.querySelector('.openimmo-property-map__status');

    if (!status) {
      return;
    }

    status.textContent = message;
    status.dataset.type = type;
    status.hidden = message === '';
  };

  const showMapFallback = (section, collection = null) => {
    const fallback = section.querySelector('.openimmo-property-map__fallback');

    if (collection) {
      renderMapList(section, collection);
    }

    if (fallback) {
      fallback.hidden = false;
    }

    section.classList.add('has-map-fallback');
    section.setAttribute('aria-busy', 'false');
  };

  const hideMapSection = (section) => {
    section.hidden = true;
    section.setAttribute('aria-hidden', 'true');
    section.setAttribute('aria-busy', 'false');
    setMapStatus(section);
    const fallback = section.querySelector('.openimmo-property-map__fallback');

    if (fallback) {
      fallback.hidden = true;
    }
  };

  const appendMapListItem = (list, feature) => {
    const properties = feature && feature.properties ? feature.properties : {};
    const item = document.createElement('a');
    const title = document.createElement('strong');

    item.className = 'openimmo-property-map__item';
    item.href = properties.permalink || '#';
    title.textContent = properties.title || '';
    item.appendChild(title);

    if (properties.address) {
      const address = document.createElement('span');
      address.textContent = properties.address;
      item.appendChild(address);
    }

    if (properties.precision === 'postal') {
      const precision = document.createElement('small');
      precision.className = 'openimmo-property-map__precision';
      precision.textContent = mapText.approximate;
      item.appendChild(precision);
    }

    list.appendChild(item);
  };

  const renderMapList = (section, collection) => {
    const features = Array.isArray(collection.features) ? collection.features : [];
    const list = section.querySelector('.openimmo-property-map__list');
    const summary = section.querySelector('.openimmo-property-map__summary');

    if (summary) {
      summary.textContent = collection.truncated
        ? mapText.truncated(features.length)
        : features.length === 1
          ? mapText.oneResult
          : mapText.manyResults(features.length);
    }

    if (!list) {
      return;
    }

    list.replaceChildren();
    features.forEach((feature) => appendMapListItem(list, feature));
  };

  const mapPopupContent = (properties) => {
    const content = document.createElement('article');
    const title = document.createElement('strong');
    const link = document.createElement('a');

    content.className = 'openimmo-property-map__popup';
    title.textContent = properties.title || '';
    content.appendChild(title);

    if (properties.address) {
      const address = document.createElement('span');
      address.textContent = properties.address;
      content.appendChild(address);
    }

    if (properties.precision === 'postal') {
      const precision = document.createElement('small');
      precision.textContent = mapText.approximate;
      content.appendChild(precision);
    }

    link.href = properties.permalink || '#';
    link.textContent = mapText.details;
    content.appendChild(link);

    return content;
  };

  const fitGoogleMapToFeatures = (map, features, maxZoom) => {
    const coordinates = features
      .map((feature) => feature && feature.geometry && feature.geometry.coordinates)
      .filter((coordinate) => Array.isArray(coordinate) && coordinate.length >= 2);

    if (!coordinates.length) {
      return;
    }

    if (coordinates.length === 1) {
      map.setCenter({ lat: Number(coordinates[0][1]), lng: Number(coordinates[0][0]) });
      map.setZoom(Math.min(maxZoom, 14));
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    coordinates.forEach((coordinate) => {
      bounds.extend({ lat: Number(coordinate[1]), lng: Number(coordinate[0]) });
    });
    map.fitBounds(bounds, 48);

    window.google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      if (Number(map.getZoom()) > Math.min(maxZoom, 14)) {
        map.setZoom(Math.min(maxZoom, 14));
      }
    });
  };

  const googleMarkerIcon = (color, scale, opacity = 1) => ({
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: color,
    fillOpacity: opacity,
    strokeColor: '#ffffff',
    strokeOpacity: 1,
    strokeWeight: 2,
    scale
  });

  const initializeGoogleMap = async (section, collection, maxZoom) => {
    const canvas = section.querySelector('.openimmo-property-map__canvas');
    const features = Array.isArray(collection.features) ? collection.features : [];

    if (!canvas || !features.length) {
      return;
    }

    await loadGoogleMaps();
    const MarkerClusterer = await loadGoogleMarkerClusterer();
    const accent = getComputedStyle(section).getPropertyValue('--openimmo-map-marker').trim() || '#134061';
    const postal = getComputedStyle(section).getPropertyValue('--openimmo-map-postal').trim() || '#777777';
    const map = new window.google.maps.Map(canvas, {
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false,
      maxZoom,
      gestureHandling: 'cooperative'
    });
    const infoWindow = new window.google.maps.InfoWindow();
    const markers = [];

    features.forEach((feature) => {
      const coordinates = Array.isArray(feature?.geometry?.coordinates)
        ? feature.geometry.coordinates
        : null;
      const properties = feature?.properties && typeof feature.properties === 'object'
        ? feature.properties
        : {};

      if (!coordinates || coordinates.length < 2) {
        return;
      }

      const position = { lat: Number(coordinates[1]), lng: Number(coordinates[0]) };

      if (properties.precision === 'postal') {
        new window.google.maps.Circle({
          center: position,
          radius: 800,
          strokeOpacity: 0,
          fillColor: postal,
          fillOpacity: 0.15,
          map
        });
      }

      const marker = new window.google.maps.Marker({
        position,
        icon: properties.precision === 'postal'
          ? googleMarkerIcon(postal, 9, 0.72)
          : googleMarkerIcon(accent, 7),
        title: String(properties.title || '')
      });
      marker.addListener('click', () => {
        infoWindow.setContent(mapPopupContent(properties));
        infoWindow.open({ anchor: marker, map });
      });
      markers.push(marker);
    });

    if (MarkerClusterer) {
      new MarkerClusterer({ map, markers });
    } else {
      markers.forEach((marker) => marker.setMap(map));
    }

    fitGoogleMapToFeatures(map, features, maxZoom);
    section.classList.remove('has-map-fallback');
    section.classList.add('is-map-ready');
    setMapStatus(section);
    section.setAttribute('aria-busy', 'false');

    const fallback = section.querySelector('.openimmo-property-map__fallback');
    if (fallback && section.dataset.showList === 'false') {
      fallback.hidden = true;
    }
  };

  const fitMapToFeatures = (map, features, maxZoom) => {
    const coordinates = features
      .map((feature) => feature && feature.geometry && feature.geometry.coordinates)
      .filter((coordinate) => Array.isArray(coordinate) && coordinate.length >= 2);

    if (coordinates.length === 1) {
      map.jumpTo({ center: coordinates[0], zoom: Math.min(maxZoom, 14) });
      return;
    }

    if (coordinates.length > 1) {
      const bounds = coordinates.reduce(
        (current, coordinate) => current.extend(coordinate),
        new maplibregl.LngLatBounds(coordinates[0], coordinates[0])
      );
      map.fitBounds(bounds, { padding: 48, maxZoom: Math.min(maxZoom, 14), duration: 0 });
    }
  };

  const addMapLayers = (map, collection, section, maxZoom) => {
    const accent = getComputedStyle(section).getPropertyValue('--openimmo-map-marker').trim() || '#134061';
    const postal = getComputedStyle(section).getPropertyValue('--openimmo-map-postal').trim() || '#777777';

    map.addSource('openimmo-properties', {
      type: 'geojson',
      data: collection,
      cluster: true,
      clusterMaxZoom: Math.max(1, Math.min(maxZoom - 1, 14)),
      clusterRadius: 46
    });
    map.addLayer({
      id: 'openimmo-clusters',
      type: 'circle',
      source: 'openimmo-properties',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': accent,
        'circle-radius': ['step', ['get', 'point_count'], 18, 25, 24, 100, 30],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2
      }
    });
    map.addLayer({
      id: 'openimmo-cluster-count',
      type: 'symbol',
      source: 'openimmo-properties',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['Noto Sans Bold'],
        'text-size': 12
      },
      paint: { 'text-color': '#ffffff' }
    });
    map.addLayer({
      id: 'openimmo-points-exact',
      type: 'circle',
      source: 'openimmo-properties',
      filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'precision'], 'exact']],
      paint: {
        'circle-color': accent,
        'circle-radius': 8,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2
      }
    });
    map.addLayer({
      id: 'openimmo-points-postal',
      type: 'circle',
      source: 'openimmo-properties',
      filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'precision'], 'postal']],
      paint: {
        'circle-color': postal,
        'circle-opacity': 0.72,
        'circle-radius': 11,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2
      }
    });

    map.on('click', 'openimmo-clusters', async (event) => {
      const cluster = map.queryRenderedFeatures(event.point, { layers: ['openimmo-clusters'] })[0];
      const clusterId = cluster && cluster.properties ? cluster.properties.cluster_id : null;

      if (clusterId === null) {
        return;
      }

      try {
        const zoom = await map.getSource('openimmo-properties').getClusterExpansionZoom(clusterId);
        map.easeTo({ center: cluster.geometry.coordinates, zoom });
      } catch (error) {
        // A rapidly changing source may invalidate a cluster between click and zoom.
      }
    });

    ['openimmo-points-exact', 'openimmo-points-postal'].forEach((layer) => {
      map.on('click', layer, (event) => {
        const feature = event.features && event.features[0];
        if (!feature || !feature.geometry || !feature.properties) {
          return;
        }

        new maplibregl.Popup({ offset: 12, closeButton: true })
          .setLngLat(feature.geometry.coordinates)
          .setDOMContent(mapPopupContent(feature.properties))
          .addTo(map);
      });
      map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = ''; });
    });
    map.on('mouseenter', 'openimmo-clusters', () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'openimmo-clusters', () => { map.getCanvas().style.cursor = ''; });
  };

  const initializeMap = async (section, collection) => {
    const canvas = section.querySelector('.openimmo-property-map__canvas');
    const features = Array.isArray(collection.features) ? collection.features : [];
    const provider = String(section.dataset.mapProvider || config.mapProvider || 'maplibre');
    const styleUrl = section.dataset.styleUrl || '';
    const maxZoom = Math.max(1, Math.min(22, Number(section.dataset.maxZoom) || 15));

    if (section.dataset.showList !== 'false') {
      renderMapList(section, collection);
    }

    if (!features.length) {
      setMapStatus(section, mapText.empty, 'empty');
      showMapFallback(section, collection);
      return;
    }

    if (provider === 'google') {
      if (!canvas || !googleMapsApiKey) {
        hideMapSection(section);
        return;
      }

      try {
        await initializeGoogleMap(section, collection, maxZoom);
      } catch (error) {
        hideMapSection(section);
      }
      return;
    }

    if (!styleUrl) {
      setMapStatus(section, mapText.missingStyle, 'error');
      showMapFallback(section, collection);
      return;
    }

    try {
      await loadMapLibre();
    } catch (error) {
      setMapStatus(section, mapText.error, 'error');
      showMapFallback(section, collection);
      return;
    }

    if (!canvas || !maplibregl.supported({ failIfMajorPerformanceCaveat: true })) {
      setMapStatus(section, mapText.error, 'error');
      showMapFallback(section, collection);
      return;
    }

    try {
      const map = new maplibregl.Map({
        container: canvas,
        style: styleUrl,
        maxZoom,
        attributionControl: false
      });
      let ready = false;
      const loadTimeout = window.setTimeout(() => {
        if (!ready) {
          setMapStatus(section, mapText.error, 'error');
          showMapFallback(section, collection);
          map.remove();
        }
      }, 20000);

      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
      map.addControl(new maplibregl.AttributionControl({
        compact: false
      }));
      map.once('load', () => {
        ready = true;
        window.clearTimeout(loadTimeout);
        section.classList.remove('has-map-fallback');
        section.classList.add('is-map-ready');
        map.resize();
        addMapLayers(map, collection, section, maxZoom);
        fitMapToFeatures(map, features, maxZoom);
        setMapStatus(section);
        section.setAttribute('aria-busy', 'false');

        const fallback = section.querySelector('.openimmo-property-map__fallback');
        if (fallback && section.dataset.showList === 'false') {
          fallback.hidden = true;
        }
      });
      map.on('error', () => {
        if (!ready) {
          setMapStatus(section, mapText.error, 'error');
          showMapFallback(section, collection);
        }
      });
    } catch (error) {
      setMapStatus(section, mapText.error, 'error');
      showMapFallback(section, collection);
    }
  };

  const fetchMapCollection = async (endpoint) => {
    const url = new URL(endpoint, window.location.href);
    const features = [];
    const featureIds = new Set();
    let firstCollection = null;
    let page = 1;
    let truncated = false;

    while (page <= mapMaxPages) {
      if (page > 1) {
        url.searchParams.set('page', String(page));
      }

      const response = await fetch(url.href, {
        headers: { Accept: 'application/geo+json, application/json' },
        credentials: 'same-origin'
      });
      const collection = await response.json();

      if (!response.ok || collection.type !== 'FeatureCollection' || !Array.isArray(collection.features)) {
        throw new Error('Invalid map response');
      }

      firstCollection ||= collection;
      collection.features.forEach((feature) => {
        const id = Number(feature?.id);
        if (Number.isSafeInteger(id) && id > 0) {
          if (featureIds.has(id)) {
            return;
          }
          featureIds.add(id);
        }

        features.push(feature);
      });
      truncated = truncated || collection.truncated === true;

      if (collection.hasMore !== true) {
        break;
      }

      const currentPage = Number(collection.page);
      const nextPage = Number(collection.nextPage);
      if (
        !Number.isInteger(currentPage)
        || !Number.isInteger(nextPage)
        || currentPage !== page
        || nextPage !== page + 1
        || nextPage > mapMaxPages
      ) {
        throw new Error('Invalid map pagination');
      }

      page = nextPage;
    }

    return {
      ...firstCollection,
      features,
      hasMore: false,
      nextPage: null,
      truncated
    };
  };

  const initPropertyGalleries = () => {
    const galleryImages = new Map();
    let lightboxGallery = null;
    let lightboxIndex = 0;
    let pendingImage = null;

    document.querySelectorAll('[data-openimmo-property-gallery]').forEach((gallery) => {
      if (gallery.dataset.openimmoGalleryInitialized === 'true') {
        return;
      }

      gallery.dataset.openimmoGalleryInitialized = 'true';
      const stage = gallery.querySelector('.wp-block-gallery.has-nested-images');
      const items = stage
        ? Array.from(stage.querySelectorAll(':scope > .openimmo-gallery-item'))
        : [];
      const previous = gallery.querySelector('[data-openimmo-gallery-previous]');
      const next = gallery.querySelector('[data-openimmo-gallery-next]');
      const position = gallery.querySelector('[data-openimmo-gallery-position]');
      const open = gallery.querySelector('[data-openimmo-gallery-open]');
      const controls = gallery.querySelector('[data-openimmo-gallery-controls]');
      const isHorizontal = gallery.classList.contains('openimmo-gallery-arrangement-focus')
        || gallery.classList.contains('openimmo-gallery-arrangement-strip');
      let activeIndex = 0;
      let updateFrame = 0;

      if (!stage || items.length === 0) {
        return;
      }

      galleryImages.set(gallery, items.map((item) => item.querySelector('img')));

      if (!controls) {
        return;
      }

      const findActiveIndex = () => {
        if (!isHorizontal) {
          return 0;
        }

        if (stage.scrollLeft <= 1) {
          return 0;
        }

        if (stage.scrollWidth - stage.clientWidth - stage.scrollLeft <= 1) {
          return items.length - 1;
        }

        const stageCenter = stage.scrollLeft + (stage.clientWidth / 2);
        let closestDistance = Number.POSITIVE_INFINITY;
        let closestIndex = 0;

        items.forEach((item, index) => {
          const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
          const distance = Math.abs(stageCenter - itemCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      };

      const hasHiddenItems = () => {
        const stageRect = stage.getBoundingClientRect();
        const renderedItems = items.filter((item) => {
          const style = window.getComputedStyle(item);
          const rect = item.getBoundingClientRect();

          return style.display !== 'none'
            && style.visibility !== 'hidden'
            && rect.width > 0
            && rect.height > 0;
        });

        if (renderedItems.length !== items.length) {
          return true;
        }

        return renderedItems.some((item) => {
          const rect = item.getBoundingClientRect();
          const left = rect.left - stageRect.left + stage.scrollLeft;
          const top = rect.top - stageRect.top + stage.scrollTop;
          const right = left + rect.width;
          const bottom = top + rect.height;
          const tolerance = 2;

          return left < -tolerance
            || top < -tolerance
            || right > stage.clientWidth + tolerance
            || bottom > stage.clientHeight + tolerance;
        });
      };

      const updateControls = () => {
        updateFrame = 0;
        activeIndex = findActiveIndex();
        const galleryHasHiddenItems = hasHiddenItems();

        gallery.classList.toggle('openimmo-gallery-has-hidden-items', galleryHasHiddenItems);
        if (controls) {
          controls.hidden = !galleryHasHiddenItems;
        }
        if (open) {
          open.hidden = !galleryHasHiddenItems;
        }
        if (previous) {
          previous.hidden = !galleryHasHiddenItems;
        }
        if (next) {
          next.hidden = !galleryHasHiddenItems;
        }
        if (position) {
          position.hidden = !galleryHasHiddenItems;
        }

        if (position) {
          position.textContent = `${activeIndex + 1} / ${items.length}`;
        }
        if (previous) {
          previous.disabled = activeIndex === 0;
        }
        if (next) {
          next.disabled = activeIndex === items.length - 1;
        }
      };

      const queueControlUpdate = () => {
        if (!updateFrame) {
          updateFrame = window.requestAnimationFrame(updateControls);
        }
      };

      const scrollToItem = (index) => {
        const target = items[Math.max(0, Math.min(items.length - 1, index))];

        if (!target) {
          return;
        }

        stage.scrollTo({
          behavior: 'smooth',
          left: target.offsetLeft - ((stage.clientWidth - target.offsetWidth) / 2)
        });
      };

      previous?.addEventListener('click', () => scrollToItem(findActiveIndex() - 1));
      next?.addEventListener('click', () => scrollToItem(findActiveIndex() + 1));
      open?.addEventListener('click', () => {
        const target = items[isHorizontal ? findActiveIndex() : 0];
        const lightboxTrigger = target?.querySelector('.lightbox-trigger')
          || target?.querySelector('[data-wp-on--click="actions.showLightbox"]');

        lightboxTrigger?.click();
      });

      if (isHorizontal) {
        stage.addEventListener('scroll', queueControlUpdate, { passive: true });
      }

      window.addEventListener('resize', queueControlUpdate, { passive: true });
      if (typeof window.ResizeObserver === 'function') {
        const resizeObserver = new window.ResizeObserver(queueControlUpdate);
        resizeObserver.observe(stage);
      }
      stage.querySelectorAll('img').forEach((image) => {
        if (!image.complete) {
          image.addEventListener('load', queueControlUpdate, { once: true });
        }
      });

      updateControls();
    });

    if (galleryImages.size === 0) {
      return;
    }

    document.addEventListener('click', (event) => {
      const item = event.target.closest?.('.openimmo-gallery-item');
      const gallery = item?.closest('[data-openimmo-property-gallery]');

      if (galleryImages.has(gallery)) {
        lightboxGallery = gallery;
        lightboxIndex = Array.from(item.parentElement.children).indexOf(item);
      }
    }, true);

    const navigateLoadedImage = (event, direction) => {
      const overlay = document.querySelector('.wp-lightbox-overlay.active');
      const images = galleryImages.get(lightboxGallery);

      if (!overlay || !images || images.length < 2) {
        return;
      }

      const overlayImage = overlay.querySelector('.lightbox-image-container img');
      const currentIndex = images.findIndex((image) => image && overlayImage
        && (image.src === overlayImage.src || image.currentSrc === overlayImage.currentSrc));

      if (currentIndex < 0) {
        return;
      }

      lightboxIndex = currentIndex;

      const targetIndex = (lightboxIndex + direction + images.length) % images.length;
      const targetImage = images[targetIndex];

      if (!targetImage || (targetImage.naturalWidth > 0 && targetImage.naturalHeight > 0)) {
        lightboxIndex = targetIndex;
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (pendingImage === targetImage) {
        return;
      }

      pendingImage = targetImage;
      const requestedGallery = lightboxGallery;
      const currentSource = overlayImage.src;
      targetImage.loading = 'eager';
      targetImage.decode().then(() => {
        if (document.querySelector('.wp-lightbox-overlay.active') === overlay
          && lightboxGallery === requestedGallery
          && pendingImage === targetImage
          && overlay.querySelector('.lightbox-image-container img')?.src === currentSource
          && targetImage.naturalWidth > 0) {
          overlay.querySelector(direction > 0
            ? '.wp-lightbox-navigation-button-next'
            : '.wp-lightbox-navigation-button-prev')?.click();
        }
      }).catch(() => {
        // Keep the current image visible if the next image cannot be decoded.
      }).finally(() => {
        if (pendingImage === targetImage) {
          pendingImage = null;
        }
      });
    };

    document.addEventListener('click', (event) => {
      if (event.target.closest?.('.wp-lightbox-navigation-button-next')) {
        navigateLoadedImage(event, 1);
      } else if (event.target.closest?.('.wp-lightbox-navigation-button-prev')) {
        navigateLoadedImage(event, -1);
      }
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        navigateLoadedImage(event, 1);
      } else if (event.key === 'ArrowLeft') {
        navigateLoadedImage(event, -1);
      }
    }, true);
  };

  const initPropertyMaps = () => {
    document.querySelectorAll('[data-openimmo-property-map]').forEach(async (section) => {
      const endpoint = section.dataset.endpoint || '';

      if (!endpoint) {
        setMapStatus(section, mapText.error, 'error');
        showMapFallback(section);
        return;
      }

      try {
        const collection = await fetchMapCollection(endpoint);
        await initializeMap(section, collection);
      } catch (error) {
        setMapStatus(section, mapText.error, 'error');
        showMapFallback(section);
      }
    });
  };

  const markerMapLink = (marker) => {
    const link = document.createElement('a');
    link.href = marker.url;
    link.textContent = marker.title;
    if (marker.newTab) {
      link.target = '_blank';
    }
    const rel = [];
    if (marker.newTab || marker.noopener) rel.push('noopener');
    if (marker.noreferrer) rel.push('noreferrer');
    if (rel.length) link.rel = rel.join(' ');
    return link;
  };

  const markerMapPopup = (marker) => {
    const popup = document.createElement('article');
    const title = document.createElement('strong');
    popup.className = 'openimmo-property-map__popup';
    title.textContent = marker.title;
    popup.appendChild(title);
    if (marker.content) {
      const body = document.createElement('div');
      body.className = 'openimmo-marker-map__popup-content';
      body.innerHTML = marker.content;
      popup.appendChild(body);
    }
    if (marker.url) {
      const link = markerMapLink(marker);
      link.textContent = markerMapText.link || 'Link öffnen';
      popup.appendChild(link);
    }
    return popup;
  };

  const markerMapActivate = (marker, openPopup) => {
    if (marker.popup) {
      openPopup();
    } else if (marker.url) {
      markerMapLink(marker).click();
    }
  };

  const initMarkerMap = async (section) => {
    const canvas = section.querySelector('.openimmo-property-map__canvas');
    const data = section.querySelector('.openimmo-marker-map__data');
    const fallback = section.querySelector('.openimmo-property-map__fallback');
    if (!canvas || !data) return;
    let markers;
    try {
      markers = JSON.parse(data.textContent || '[]');
    } catch (error) {
      markers = [];
    }
    if (!Array.isArray(markers) || !markers.length) {
      setMapStatus(section, markerMapText.empty || 'Es sind noch keine vollständigen Marker eingetragen.', 'empty');
      section.setAttribute('aria-busy', 'false');
      canvas.hidden = true;
      return;
    }
    const maxZoom = Math.max(1, Math.min(22, Number(section.dataset.maxZoom) || 15));
    const color = getComputedStyle(section).getPropertyValue('--openimmo-map-marker').trim() || '#134061';
    const coordinates = markers.map((marker) => [Number(marker.longitude), Number(marker.latitude)]);
    try {
      if (section.dataset.mapProvider === 'google') {
        await loadGoogleMaps();
        const map = new window.google.maps.Map(canvas, {
          mapTypeControl: false, fullscreenControl: false, streetViewControl: false,
          maxZoom, gestureHandling: 'cooperative'
        });
        const infoWindow = new window.google.maps.InfoWindow();
        markers.forEach((marker) => {
          const position = { lat: Number(marker.latitude), lng: Number(marker.longitude) };
          const pin = new window.google.maps.Marker({
            map, position, title: marker.title,
            icon: section.dataset.markerShape === 'circle'
              ? googleMarkerIcon(color, 9)
              : { path: 'M 0,-18 C -10,-18 -16,-11 -16,-2 C -16,8 0,18 0,18 C 0,18 16,8 16,-2 C 16,-11 10,-18 0,-18 Z',
                fillColor: color, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2, scale: 0.8 }
          });
          pin.addListener('click', () => markerMapActivate(marker, () => {
            infoWindow.setContent(markerMapPopup(marker));
            infoWindow.open({ anchor: pin, map });
          }));
        });
        fitGoogleMapToFeatures(map, markers.map((_, index) => ({ geometry: { coordinates: coordinates[index] } })), maxZoom);
      } else {
        if (!section.dataset.styleUrl) throw new Error('Missing map style');
        await loadMapLibre();
        if (!maplibregl.supported({ failIfMajorPerformanceCaveat: true })) throw new Error('Map unavailable');
        const map = new maplibregl.Map({
          container: canvas, style: section.dataset.styleUrl, maxZoom, attributionControl: false
        });
        await new Promise((resolve, reject) => {
          const timeout = window.setTimeout(() => reject(new Error('Map timed out')), 20000);
          map.once('load', () => { window.clearTimeout(timeout); resolve(); });
          map.once('error', () => { window.clearTimeout(timeout); reject(new Error('Map unavailable')); });
        });
        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
        map.addControl(new maplibregl.AttributionControl({ compact: false }));
        markers.forEach((marker, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `openimmo-marker-map__marker is-${section.dataset.markerShape === 'circle' ? 'circle' : 'pin'}`;
          button.title = marker.title;
          button.setAttribute('aria-label', marker.title);
          button.style.setProperty('--openimmo-map-marker', color);
          const mapMarker = new maplibregl.Marker({ element: button, anchor: 'bottom' })
            .setLngLat(coordinates[index]).addTo(map);
          button.addEventListener('click', () => markerMapActivate(marker, () => {
            new maplibregl.Popup({ offset: 18, closeButton: true })
              .setLngLat(mapMarker.getLngLat()).setDOMContent(markerMapPopup(marker)).addTo(map);
          }));
        });
        fitMapToFeatures(map, markers.map((_, index) => ({ geometry: { coordinates: coordinates[index] } })), maxZoom);
        map.resize();
      }
      setMapStatus(section);
      section.setAttribute('aria-busy', 'false');
      if (fallback) fallback.hidden = true;
    } catch (error) {
      setMapStatus(section, markerMapText.error || 'Die Karte konnte nicht geladen werden. Die Liste der Standorte bleibt verfügbar.', 'error');
      section.setAttribute('aria-busy', 'false');
      canvas.hidden = true;
    }
  };

  const initMarkerMaps = () => {
    document.querySelectorAll('[data-openimmo-marker-map]').forEach(initMarkerMap);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initContactForms();
      initSearchAgentForms();
      initPdfExposeForms();
      initWithdrawalForms();
      initFavorites();
      initPropertyGalleries();
      initPropertyMaps();
      initMarkerMaps();
    });
  } else {
    initContactForms();
    initSearchAgentForms();
    initPdfExposeForms();
    initWithdrawalForms();
    initFavorites();
    initPropertyGalleries();
    initPropertyMaps();
    initMarkerMaps();
  }
})();
